// ABOUTME: Tier-1 interview metadata editing (#30) — details form prefilled from the record
// ABOUTME: Group changes re-point the classification; no re-analysis needed for metadata

import { useEffect, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog'
import { InterviewDetail, InterviewPatch, patchInterview } from '@/api/interviews'
import { usePersonas } from '@/hooks/usePersonas'

const INSTITUTIONS = ['ALX', 'ALU', 'ALA', 'FIT']
const FIELD = 'w-full border border-gray-300 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-alx-green/40'

export function EditInterviewDialog({ interview, open, onClose }: {
  interview: InterviewDetail
  open: boolean
  onClose: () => void
}) {
  const queryClient = useQueryClient()
  const { data: personas } = usePersonas()
  const groups = (personas?.definitions ?? []).map((d) => d.persona_type)
  const [form, setForm] = useState<InterviewPatch>({})
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    setError(null)
    setForm({
      interviewee_name: interview.interviewee,
      interview_name: interview.name ?? '',
      date: interview.date ?? '',
      facilitator: interview.facilitator ?? '',
      notetaker: interview.notetaker ?? '',
      recording_link: interview.recording_link ?? '',
      institution: interview.institution ?? '',
      group: interview.group ?? '',
    })
    // Deliberately only re-seed when the dialog OPENS — a background refetch of the
    // interview mid-edit must not wipe in-progress form state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const set = (k: keyof InterviewPatch) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
      setForm((f) => ({ ...f, [k]: e.target.value }))

  const save = async () => {
    setSaving(true)
    setError(null)
    try {
      // Send only changed fields
      const current: Record<string, string> = {
        interviewee_name: interview.interviewee,
        interview_name: interview.name ?? '',
        date: interview.date ?? '',
        facilitator: interview.facilitator ?? '',
        notetaker: interview.notetaker ?? '',
        recording_link: interview.recording_link ?? '',
        institution: interview.institution ?? '',
        group: interview.group ?? '',
      }
      const patch: InterviewPatch = {}
      for (const [k, v] of Object.entries(form)) {
        // Never send an emptied select — "Select…" is a placeholder, not a value
        if ((k === 'group' || k === 'institution') && !(v ?? '').trim()) continue
        if ((v ?? '') !== current[k]) (patch as any)[k] = v
      }
      if (Object.keys(patch).length === 0) {
        onClose()
        return
      }
      await patchInterview(interview.interview_id, patch)
      queryClient.invalidateQueries({ queryKey: ['interview', interview.interview_id] })
      queryClient.invalidateQueries({ queryKey: ['interviews'] })
      onClose()
    } catch (e: any) {
      setError(e?.response?.data?.detail ?? e?.message ?? 'Save failed')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Edit interview details</DialogTitle>
          <DialogDescription>
            Metadata only — the transcript and analysis are untouched. To correct the
            transcript itself, use "Edit transcript" on the raw transcript tab.
          </DialogDescription>
        </DialogHeader>

        {error && <p className="text-sm text-red-600">{typeof error === 'string' ? error : 'Save failed'}</p>}

        <div className="grid grid-cols-2 gap-3">
          <label className="block col-span-2">
            <span className="block text-sm font-medium text-gray-700 mb-1">Interviewee</span>
            <Input value={form.interviewee_name ?? ''} onChange={set('interviewee_name')} />
          </label>
          <label className="block col-span-2">
            <span className="block text-sm font-medium text-gray-700 mb-1">Interview name</span>
            <Input value={form.interview_name ?? ''} onChange={set('interview_name')} />
          </label>
          <label className="block">
            <span className="block text-sm font-medium text-gray-700 mb-1">Group</span>
            <select value={form.group ?? ''} onChange={set('group')} className={FIELD}>
              <option value="">Select…</option>
              {groups.map((g) => <option key={g} value={g}>{g}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="block text-sm font-medium text-gray-700 mb-1">Institution</span>
            <select value={form.institution ?? ''} onChange={set('institution')} className={FIELD}>
              <option value="">Select…</option>
              {INSTITUTIONS.map((i) => <option key={i} value={i}>{i}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="block text-sm font-medium text-gray-700 mb-1">Date</span>
            <input type="date" value={form.date ?? ''} onChange={set('date')} className={FIELD} />
          </label>
          <label className="block">
            <span className="block text-sm font-medium text-gray-700 mb-1">Recording link</span>
            <Input value={form.recording_link ?? ''} onChange={set('recording_link')} />
          </label>
          <label className="block">
            <span className="block text-sm font-medium text-gray-700 mb-1">Facilitator</span>
            <Input value={form.facilitator ?? ''} onChange={set('facilitator')} />
          </label>
          <label className="block">
            <span className="block text-sm font-medium text-gray-700 mb-1">Notetaker</span>
            <Input value={form.notetaker ?? ''} onChange={set('notetaker')} />
          </label>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save details'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
